import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { ErrorBanner, formatMoney, InlineSpinner } from '@/components/common'

interface CustomerChange {
  changeNumber: string
  orderNumber: string
  eventDate: string
  customerName: string
  summary: string
  reason: string
  additionalAmount: number
  status: string
  answerable: boolean
  answeredAt: string | null
}

/**
 * A change to an agreed order, put to the customer who agreed it.
 *
 * <p>Reached from a link and nothing else - a customer is not a user of this
 * system and holds no password in it, so the token in the address is the whole
 * of the credential, the way the quotation link works.</p>
 *
 * <p>It says one thing and asks one question. Somebody reading it on a phone
 * between other things needs to know what is changing, what it costs, and
 * which of two buttons to press.</p>
 */
export function OrderChangeApprovalPage() {
  const { token = '' } = useParams()
  const [name, setName] = useState('')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['order-change', token] as const,
    queryFn: () => api.publicGet<CustomerChange>(`/api/v1/public/order-changes/${token}`),
    retry: false,
  })

  const decide = useMutation({
    mutationFn: (accepted: boolean) =>
      api.publicPost<CustomerChange>(`/api/v1/public/order-changes/${token}/decision`, {
        accepted,
        name: name.trim(),
      }),
    onSuccess: () => refetch(),
  })

  if (isLoading) return <InlineSpinner />

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <ErrorBanner error={error} />
      </Container>
    )
  }
  if (!data) return null

  const answered = data.status !== 'PENDING'

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            {data.orderNumber} · {data.eventDate}
          </Typography>
          <Typography variant="h6" gutterBottom>
            A change to your order
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.customerName}, we would like to change one thing about what we are preparing for
            you. Nothing changes unless you agree to it.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1}>
            <Typography variant="subtitle1">{data.summary}</Typography>
            <Typography variant="body2" color="text.secondary">
              {data.additionalAmount > 0
                ? `This adds ${formatMoney(data.additionalAmount)} to your bill.`
                : 'This adds nothing to your bill.'}
            </Typography>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {answered ? (
            <Alert
              severity={data.status === 'APPROVED' ? 'success' : 'info'}
              icon={data.status === 'APPROVED' ? <CheckCircleIcon /> : undefined}
            >
              {data.status === 'APPROVED'
                ? 'Thank you — we have this agreed, and your order has been updated.'
                : 'You declined this change. Your order is unchanged.'}
            </Alert>
          ) : !data.answerable ? (
            <Alert severity="warning">
              This can no longer be answered here — the date it was needed by has passed. Please
              call the office.
            </Alert>
          ) : (
            <>
              <ErrorBanner error={decide.error} />
              <TextField
                fullWidth
                required
                size="small"
                label="Your name"
                helperText="Kept with your answer, so we know who agreed it"
                value={name}
                onChange={(event) => setName(event.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  disabled={!name.trim() || decide.isPending}
                  onClick={() => decide.mutate(true)}
                >
                  Yes, go ahead
                </Button>
                <Button
                  variant="outlined"
                  disabled={!name.trim() || decide.isPending}
                  onClick={() => decide.mutate(false)}
                >
                  No, leave it as it is
                </Button>
              </Box>
            </>
          )}

          <Chip
            size="small"
            variant="outlined"
            label={data.changeNumber}
            sx={{ mt: 3 }}
          />
        </CardContent>
      </Card>
    </Container>
  )
}
